import { useContext, useEffect, useMemo } from 'react';

import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack, Typography } from '@mui/material';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import MainNav from 'src/components/MainNav';
import PageHeader from 'src/components/PageHeader';
import RepeatingVisitCard from 'src/components/RepeatingVisitCard';
import StyledButtonWithStartIconComponent from 'src/components/StyledButtonWithStartIcon';
import { AnalyticsContext } from 'src/contexts/AnalyticsContext';
import useResponsive from 'src/hooks/useResponsive';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useGetVisitSchedulesQuery, type VisitSchedule } from 'src/store/sageHomeApi';

export const Route = createFileRoute('/(app)/visits/repeating-visits')({
    component: RepeatingVisitListComponent,
});

const PageName = 'Repeating Visit List';

function RepeatingVisitListComponent() {
    const { isMobile } = useResponsive();
    const { trackPageView } = useContext(AnalyticsContext);
    const navigate = useNavigate();
    const { careRecipient } = useTeamMemberData();

    const {
        data: visitSchedules,
        isLoading,
        error,
    } = useGetVisitSchedulesQuery(
        {
            careRecipientRid: careRecipient?.teamMemberRid || '',
        },
        {
            skip: !careRecipient?.teamMemberRid,
        }
    );

    // Filter to only show repeating visits (those with cronExpression)
    const repeatingSchedules = useMemo(() => {
        if (!visitSchedules) return [];
        return visitSchedules.filter((schedule: VisitSchedule) => schedule.cronExpression);
    }, [visitSchedules]);

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditClick = (visitScheduleRid: string) => {
        navigate({
            to: '/visits/edit/$visitScheduleRid',
            params: { visitScheduleRid },
        });
    };

    return (
        <InnerMaxWidthContainer>
            <PageHeader pageTitle="Visit Schedule" onBack={() => navigate({ to: '/visits' })} />

            <Stack sx={{ gap: 2 }}>
                {isLoading ? (
                    <Typography variant="body1">Loading...</Typography>
                ) : error ? (
                    <Typography color="error" variant="body1">
                        Error loading visit schedules. Please try again.
                    </Typography>
                ) : repeatingSchedules.length === 0 ? (
                    <Stack sx={{ gap: 4 }}>
                        <Typography variant="body1">No repeating visits.</Typography>
                        <StyledButtonWithStartIconComponent
                            variant="contained"
                            color="primary"
                            onClick={() =>
                                navigate({ to: '/visits/add', search: { type: 'repeating' } })
                            }
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                        >
                            Add new repeating visit
                        </StyledButtonWithStartIconComponent>
                    </Stack>
                ) : (
                    repeatingSchedules.map((schedule: VisitSchedule) => (
                        <RepeatingVisitCard
                            key={schedule.visitScheduleRid}
                            schedule={schedule}
                            onEditClick={handleEditClick}
                        />
                    ))
                )}
            </Stack>

            {isMobile && <MainNav />}
        </InnerMaxWidthContainer>
    );
}
